import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import ScrollHint from '../components/common/ScrollHint';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import UserAvatar from '../components/UserAvatar';
import CtcOutlierBanner from '../components/CtcOutlierBanner';
import {
  Users2,
  TrendingUp,
  TrendingDown,
  Trophy,
  Target,
  AlertTriangle,
  CheckCircle2,
  Briefcase,
  Loader2,
  RefreshCw,
  ArrowRight,
  Calendar,
  Clock,
  IndianRupee,
  ChevronDown,
  ChevronRight,
  Network,
  Minus,
  Snowflake,
  Search,
  X,
  Star,
  Coffee,
} from 'lucide-react';
import { myTeamApi, getErrorMessage } from '../services/api';
import { formatINR } from '../components/command-center/constants';
import MissingCtcBanner from '../components/MissingCtcBanner';
import usePersistedState from '../hooks/usePersistedState';
import StandupCard from '../components/my-team/StandupCard';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { canSeeRevenue } from '../lib/perms';
import DateRangePicker from '../components/DateRangePicker';

const PREV_PERIOD_LABEL = {
  month: 'last month',
  quarter: 'last quarter',
  year: 'last year',
  all: 'previous',
};

// Same stage colours used by Job Allocation pipeline-bar
const STAGE_LABEL = {
  sourced: 'Sourced',
  screening: 'Screened',
  client_submission: 'Submitted',
  interview_scheduled: 'Interview',
  interview_selected: 'Selected',
  offer_released: 'Offered',
  joined: 'Joined',
};

// Tiny delta chip — shows ↑/↓/= with green/red/slate tone. Renders nothing when no value.
const DeltaChip = ({ value, suffix = '', testid, title }) => {
  if (value === null || value === undefined) return null;
  const positive = value > 0;
  const negative = value < 0;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const tone = positive
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : negative
    ? 'text-rose-700 bg-rose-50 border-rose-200'
    : 'text-slate-500 bg-slate-50 border-slate-200';
  const sign = positive ? '+' : '';
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-mono font-semibold rounded border px-1 ${tone}`}
      data-testid={testid}
      title={title}
    >
      <Icon className="w-2.5 h-2.5" />
      {sign}
      {value}
      {suffix}
    </span>
  );
};

const KpiTile = ({ label, value, icon: Icon, tone = 'slate', testid, hint, delta, deltaSuffix = '', deltaTitle }) => {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    teal: 'bg-teal-50 border-teal-200 text-teal-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
  };
  return (
    <div
      className={`rounded-lg border ${toneMap[tone]} px-3 py-2.5 flex items-start gap-3`}
      data-testid={testid}
      title={hint}
    >
      <div className="w-9 h-9 rounded-md bg-white/70 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
        <div className="text-2xl font-bold text-slate-900 leading-tight">{value}</div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {hint ? <span className="text-[10px] text-slate-500 truncate">{hint}</span> : null}
          {delta !== undefined && delta !== null ? (
            <DeltaChip
              value={delta}
              suffix={deltaSuffix}
              testid={`${testid}-delta`}
              title={deltaTitle}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Horizontal funnel — each stage as a labelled bar; widths normalised to the largest value
const FunnelBar = ({ stages }) => {
  const top = stages.reduce((m, s) => (s.count > m ? s.count : m), 0);
  if (!top) {
    return (
      <div className="text-xs text-slate-400 italic py-3" data-testid="mt-funnel-empty">
        No pipeline activity in this period.
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="mt-funnel">
      {stages.map((s) => {
        const pct = (s.count / top) * 100;
        return (
          <div key={s.stage} className="flex items-center gap-2">
            <div className="text-[11px] font-medium text-slate-600 w-20 truncate">
              {STAGE_LABEL[s.stage] || s.stage}
            </div>
            <div className="flex-1 h-5 bg-slate-100 rounded">
              <div
                className="h-5 bg-teal-500 rounded transition-all"
                style={{ width: `${Math.max(2, pct)}%` }}
                data-testid={`mt-funnel-${s.stage}`}
              />
            </div>
            <div className="text-xs font-mono font-semibold text-slate-700 w-10 text-right">
              {s.count}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const variantStyles = {
  achieved: { row: 'border-l-emerald-400', label: 'text-emerald-700', icon: CheckCircle2 },
  ontrack: { row: 'border-l-teal-400', label: 'text-teal-700', icon: TrendingUp },
  cold: { row: 'border-l-amber-400', label: 'text-amber-700', icon: Target },
  behind: { row: 'border-l-rose-400', label: 'text-rose-700', icon: AlertTriangle },
};

// Tiny SVG sparkline — 4 weekly buckets, oldest on the left. Renders as a
// smooth area chart so even small differences are visible at a glance.
const Sparkline = ({ data = [], width = 80, height = 28 }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - (v / max) * (height - 4) - 2;
    return `${x},${y}`;
  });
  const linePath = `M ${points.join(' L ')}`;
  const areaPath = `${linePath} L ${width},${height} L 0,${height} Z`;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      aria-label="4-week trend"
    >
      <path d={areaPath} fill="rgba(20, 184, 166, 0.15)" />
      <path
        d={linePath}
        fill="none"
        stroke="rgb(13, 148, 136)"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* end-cap dot for emphasis */}
      <circle
        cx={(data.length - 1) * stepX}
        cy={height - (data[data.length - 1] / max) * (height - 4) - 2}
        r="2.5"
        fill="rgb(13, 148, 136)"
      />
    </svg>
  );
};

const RANK_STYLES = {
  1: { ring: 'ring-amber-300 bg-amber-50', medal: '🥇', label: 'text-amber-700' },
  2: { ring: 'ring-slate-300 bg-slate-50', medal: '🥈', label: 'text-slate-600' },
  3: { ring: 'ring-orange-300 bg-orange-50', medal: '🥉', label: 'text-orange-700' },
};

const LeaderboardCard = ({ p, onOpen }) => {
  const style = RANK_STYLES[p.rank] || RANK_STYLES[3];
  const delta = p.delta_vs_prev_week;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`mt-leaderboard-card-${p.rank}`}
      className={`group flex items-center gap-3 rounded-lg border ring-1 ${style.ring} px-3 py-2.5 hover:shadow-sm transition text-left`}
      title={`Open ${p.name}'s performance`}
    >
      <span
        className={`flex-shrink-0 w-9 h-9 rounded-full ${style.ring} flex items-center justify-center text-base font-bold ${style.label}`}
        aria-label={`Rank ${p.rank}`}
      >
        {style.medal}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-sm font-semibold text-slate-900 truncate">{p.name}</span>
          {p.role && p.role !== 'recruiter' ? (
            <span className="text-[9px] uppercase tracking-wide bg-violet-100 text-violet-700 px-1 py-0.5 rounded">
              {p.role.replace('_', ' ')}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="font-mono font-bold text-slate-800">
            {p.closures_month}
            <span className="ml-1 font-normal text-slate-500">closures</span>
          </span>
          {delta !== 0 ? (
            <span
              className={`inline-flex items-center gap-0.5 font-mono font-semibold ${
                delta > 0 ? 'text-emerald-700' : 'text-rose-700'
              }`}
              data-testid={`mt-leaderboard-card-${p.rank}-delta`}
              title={`Last week: ${p.last_week_count} · prev week: ${p.prev_week_count}`}
            >
              {delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {delta > 0 ? '+' : ''}
              {delta}
              <span className="font-normal text-slate-500 ml-0.5">vs prev week</span>
            </span>
          ) : null}
        </div>
      </div>
      <Sparkline data={p.sparkline || []} />
    </button>
  );
};

// Cold streak entry — recruiter with active pipeline but no closure in 14+ days.
// Frame as a gentle nudge for the manager: rose tint, snowflake icon, and a
// human-readable "schedule a 1:1?" subtitle if the streak is long.
const ColdStreakCard = ({ p, onOpen }) => {
  const days = p.days_since_last_closure;
  const dayLabel = days == null
    ? 'No closures yet'
    : days <= 14
    ? `${days} days since last closure`
    : days < 60
    ? `${days} days dry · time for a 1:1?`
    : `${days} days dry · escalate?`;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`mt-cold-streak-card-${p.user_id}`}
      className="group flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2.5 hover:bg-rose-50 hover:shadow-sm transition text-left"
      title={`Open ${p.name}'s performance · ${p.active_candidates} active candidates`}
    >
      <span
        className="flex-shrink-0 w-9 h-9 rounded-full bg-white border border-rose-200 flex items-center justify-center"
        aria-label="Cold streak"
      >
        <Snowflake className="w-4 h-4 text-rose-500" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-sm font-semibold text-slate-900 truncate">{p.name}</span>
          {p.role && p.role !== 'recruiter' ? (
            <span className="text-[9px] uppercase tracking-wide bg-violet-100 text-violet-700 px-1 py-0.5 rounded">
              {p.role.replace('_', ' ')}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="font-mono font-bold text-slate-800">
            {p.active_candidates}
            <span className="ml-1 font-normal text-slate-500">active</span>
          </span>
          <span className="text-rose-700 font-medium" data-testid={`mt-cold-streak-card-${p.user_id}-days`}>
            · {dayLabel}
          </span>
        </div>
      </div>
    </button>
  );
};

const ReporteeRow = ({ r, idx, onOpen, showDelta, isPinned, onTogglePin, viaManagerName, isHighlighted }) => {
  const v = variantStyles[r.monthly_goal_variant] || variantStyles.cold;
  const VIcon = v.icon;
  const isRollup = !!r.is_manager_rollup;
  return (
    <div
      data-testid={`mt-reportee-row-${idx}`}
      data-rollup={isRollup ? 'true' : undefined}
      data-pinned={isPinned ? 'true' : undefined}
      data-highlighted={isHighlighted ? 'true' : undefined}
      data-breakdown-uid={r.user_id}
      className={`relative w-full text-left grid grid-cols-12 gap-2 items-center px-3 py-2.5 border-l-4 ${v.row} ${
        isHighlighted
          ? 'bg-amber-100 ring-2 ring-amber-300 ring-inset'
          : isPinned ? 'bg-amber-50/60' : isRollup ? 'bg-teal-50/40' : 'bg-white'
      } hover:bg-slate-50 border-b border-slate-100 transition cursor-pointer`}
      onClick={onOpen}
      title={isRollup ? `Drill into ${r.name}'s team (${r.rollup_headcount || 0} recruiters)` : `Open ${r.name}'s performance dashboard`}
    >
      <div className="col-span-3 flex items-center gap-2 min-w-0">
        {onTogglePin && !isRollup ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(r.user_id);
            }}
            data-testid={`mt-reportee-pin-${idx}`}
            title={isPinned ? `Unpin ${r.name}` : `Pin ${r.name} to top`}
            className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center transition ${
              isPinned ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${isPinned ? 'fill-amber-400' : ''}`} />
          </button>
        ) : (
          <span className="w-5 flex-shrink-0" />
        )}
        {isRollup ? (
          <span className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 bg-teal-100 text-teal-700">
            <Users2 className="w-3.5 h-3.5" />
          </span>
        ) : (
          <UserAvatar name={r.name} size="md" />
        )}
        <div className="min-w-0">
          <div className="text-xs font-semibold text-slate-900 truncate flex items-center gap-1.5">
            {r.name}
            {viaManagerName ? (
              <span
                className="inline-flex items-center px-1.5 py-px rounded bg-violet-50 text-violet-700 text-[9px] font-medium border border-violet-200"
                title={`Reports to ${viaManagerName}`}
                data-testid={`mt-reportee-via-${idx}`}
              >
                via {viaManagerName}
              </span>
            ) : null}
            {isRollup ? (
              <span
                className="inline-flex items-center px-1.5 py-px rounded bg-teal-100 text-teal-700 text-[9px] font-semibold uppercase tracking-wide"
                data-testid={`mt-reportee-row-${idx}-rollup-badge`}
              >
                {r.role === 'delivery_manager' ? 'DM' : r.role === 'team_lead' ? 'TL' : 'Mgr'} · {r.rollup_headcount || 0}
              </span>
            ) : null}
          </div>
          <div className="text-[10px] text-slate-500 truncate">
            {isRollup ? `Team rollup · ${r.rollup_headcount || 0} recruiter${(r.rollup_headcount || 0) === 1 ? '' : 's'}` : r.email}
          </div>
        </div>
      </div>
      <div className="col-span-1 text-xs text-center">
        <div className="font-bold text-slate-800">{r.submissions_period}</div>
        {showDelta && r.submissions_delta_abs !== undefined ? (
          <div className="mt-0.5">
            <DeltaChip
              value={r.submissions_delta_abs}
              testid={`mt-reportee-row-${idx}-subs-delta`}
              title={`vs ${r.submissions_period_prev ?? 0} prev`}
            />
          </div>
        ) : (
          <div className="text-[9px] text-slate-400 uppercase tracking-wide">subs</div>
        )}
      </div>
      <div className="col-span-1 text-xs text-center">
        <div className="font-bold text-slate-800">{r.active_candidates}</div>
        <div className="text-[9px] text-slate-400 uppercase tracking-wide">active</div>
      </div>
      <div className="col-span-1 text-xs text-center">
        <div className="font-bold text-slate-800">{r.closures_period}</div>
        {showDelta && r.closures_delta_abs !== undefined ? (
          <div className="mt-0.5">
            <DeltaChip
              value={r.closures_delta_abs}
              testid={`mt-reportee-row-${idx}-closures-delta`}
              title={`vs ${r.closures_period_prev ?? 0} prev`}
            />
          </div>
        ) : (
          <div className="text-[9px] text-slate-400 uppercase tracking-wide">closed</div>
        )}
      </div>
      <div className="col-span-1 text-xs text-center">
        <div className="font-bold text-slate-800">{r.conversion_pct}%</div>
        <div className="text-[9px] text-slate-400 uppercase tracking-wide">conv</div>
      </div>
      <div className="col-span-1 text-xs text-center">
        <div className="font-bold text-slate-800">
          {r.avg_tat_days != null ? `${r.avg_tat_days}d` : '—'}
        </div>
        <div className="text-[9px] text-slate-400 uppercase tracking-wide">tat</div>
      </div>
      <div className="col-span-1 text-xs text-center">
        <div className="font-bold text-slate-800">{r.jobs_assigned}</div>
        <div className="text-[9px] text-slate-400 uppercase tracking-wide">jobs</div>
      </div>
      <div className={`col-span-2 flex items-center gap-1.5 ${v.label}`}>
        <VIcon className="w-3.5 h-3.5 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-[11px] font-mono font-semibold">
            {r.monthly_closures_done}/{r.monthly_goal}
          </div>
          <div className="text-[9px] uppercase tracking-wide truncate">
            {r.monthly_goal_variant === 'achieved'
              ? 'Goal achieved'
              : r.monthly_goal_variant === 'ontrack'
              ? 'On track'
              : r.monthly_goal_variant === 'cold'
              ? 'No closures yet'
              : 'Behind'}
          </div>
        </div>
      </div>
      <div className="col-span-1 flex justify-end">
        <ArrowRight className="w-4 h-4 text-slate-400" />
      </div>
    </div>
  );
};

const MyTeam = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const showRevenue = canSeeRevenue(currentUser);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(searchParams.get('period') || 'month');
  const [mode, setMode] = useState(searchParams.get('mode') || 'direct');
  const [compare, setCompare] = useState(searchParams.get('compare') === '1');
  // Scope: 'team' (default — manager + reportees) vs 'self' (just the manager's
  // own personal recruitment numbers). Lets a recruiting TL/DM toggle between
  // their team rollup and their personal dashboard on the same screen.
  const [scope, setScope] = useState(
    searchParams.get('scope') === 'self' ? 'self' : 'team'
  );
  // Free-text recruiter filter — matches name or email (case-insensitive).
  // Trimmed locally so trailing spaces don't accidentally hide everyone.
  const [recruiterQuery, setRecruiterQuery] = useState('');
  // Breakdown-only search: a SECOND query that DOESN'T filter (so all rows
  // stay visible for context) but instead scrolls to + highlights matches.
  // Useful in a 50-row org tree where a manager wants to find a specific
  // person without losing sight of the surrounding team.
  const [breakdownSearch, setBreakdownSearch] = useState('');
  const [flashTick, setFlashTick] = useState(0);
  // Pinned recruiter user_ids — stored in localStorage so they persist across
  // sessions. Pinned items always sort to the top of goal cards + breakdown.
  const [pinnedIds, setPinnedIds] = usePersistedState('mt-pinned-recruiters', []);
  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  const togglePin = useCallback((userId) => {
    setPinnedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  }, [setPinnedIds]);
  // "Today's standup" view — focus on pinned recruiters and their TODAY activity.
  // Toggled from the controls bar; resets when scope = 'self'.
  const [standupMode, setStandupMode] = useState(false);
  // Custom date range — overrides `period` when set. URL persists ?from=&to=.
  const initFrom = searchParams.get('from');
  const initTo = searchParams.get('to');
  const [customRange, setCustomRange] = useState(
    initFrom && initTo ? { from: new Date(initFrom), to: new Date(initTo) } : null
  );
  const rangeIso = customRange
    ? {
        start_date: customRange.from.toISOString().slice(0, 10),
        end_date: customRange.to.toISOString().slice(0, 10),
      }
    : null;
  // Tree mode: collapse/expand each group section
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // `also_recruits` is exposed on UserResponse; for users whose role is recruiter
  // we treat them as recruiting too. The toggle only makes sense for users who
  // actually wear two hats (recruiting TL/DM), so gate it on this.
  const isRecruitingUser = useMemo(() => {
    if (!currentUser) return false;
    const r = currentUser.role;
    if (r === 'recruiter') return true;
    if (r === 'team_lead' || r === 'delivery_manager') return !!currentUser.also_recruits;
    return false;
  }, [currentUser]);

  // Tree mode is only useful for admins / delivery managers — team-leads in this app
  // typically have only recruiter reportees (no sub-team-leads). Tree is also
  // meaningless when scope=self (just one row).
  const canSeeTree =
    scope === 'team' && ['admin', 'delivery_manager'].includes(currentUser?.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await myTeamApi.summary('me', period, { mode, compare, range: rangeIso, scope });
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load team summary'));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const next = new URLSearchParams(searchParams);
    if (period === 'month' || customRange) next.delete('period');
    else next.set('period', period);
    if (mode === 'direct') next.delete('mode'); else next.set('mode', mode);
    if (!compare) next.delete('compare'); else next.set('compare', '1');
    if (scope === 'team') next.delete('scope'); else next.set('scope', scope);
    if (customRange) {
      next.set('from', customRange.from.toISOString().slice(0, 10));
      next.set('to', customRange.to.toISOString().slice(0, 10));
    } else {
      next.delete('from');
      next.delete('to');
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, mode, compare, customRange, scope]);

  // Listen for CTC backfill events from the MissingCtcBanner so the Joined CTC
  // tile + funnel refresh in real-time as DMs fill in offered CTCs inline.
  useEffect(() => {
    const onBackfill = () => fetchData();
    window.addEventListener('ctc:backfilled', onBackfill);
    return () => window.removeEventListener('ctc:backfilled', onBackfill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Single source of truth when the picker changes.
  //
  // Critical: the picker labels (Last 7 / 30 / 90 Days, Last Year) are
  // ROLLING windows — they mean "today − N days → today", not "since 1st of
  // current month / quarter / year". For a while we mapped them to the
  // backend's `period` enum (`week|month|quarter|year`) which interpreted
  // them as calendar buckets, so "Last 30 Days" silently became "since the
  // 1st of this month". That misled the Joined CTC tile (and others) — so
  // every preset that has a corresponding date window goes through a custom
  // range now, and the backend `period` enum is only used for the literal
  // "All Time" case.
  const handleRangeChange = ({ preset, customRange: cr }) => {
    if (cr && cr.from && cr.to) {
      setCustomRange(cr);
      return;
    }
    if (!preset) return;
    if (preset === 'all') {
      setPeriod('all');
      setCustomRange(null);
      return;
    }
    // Map every other preset to its actual rolling window. presetToRange()
    // lives in DateRangePicker but the same math is trivial — we re-derive
    // it inline so the user sees the same window in both the chip label
    // and the API filter.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const back = { week: 6, month: 29, quarter: 89, year: 364 }[preset];
    if (back != null) {
      const from = new Date(today);
      from.setDate(from.getDate() - back);
      setCustomRange({ from, to: today });
      return;
    }
    if (preset === 'mtd' || preset === 'ytd') {
      const from = new Date(today);
      if (preset === 'mtd') from.setDate(1);
      else { from.setMonth(0); from.setDate(1); }
      setCustomRange({ from, to: today });
    }
  };

  // Helper: propagate the active scope (period OR custom range) to /my-performance.
  const buildPerfUrl = (userId) => {
    const qs = new URLSearchParams();
    qs.set('user_id', userId);
    if (customRange) {
      qs.set('from', customRange.from.toISOString().slice(0, 10));
      qs.set('to', customRange.to.toISOString().slice(0, 10));
    } else {
      qs.set('period', period);
    }
    return `/my-performance?${qs.toString()}`;
  };

  // Helper: drill into a sub-manager's team (used by manager-rollup rows in
  // Direct mode). Forces tree view so the user sees the sub-manager's whole team.
  const buildTeamUrl = (managerId) => {
    const qs = new URLSearchParams();
    qs.set('manager_id', managerId);
    qs.set('mode', 'tree');
    if (customRange) {
      qs.set('from', customRange.from.toISOString().slice(0, 10));
      qs.set('to', customRange.to.toISOString().slice(0, 10));
    } else {
      qs.set('period', period);
    }
    return `/my-team?${qs.toString()}`;
  };

  const openReportee = (r) =>
    navigate(r.is_manager_rollup ? buildTeamUrl(r.user_id) : buildPerfUrl(r.user_id));

  const k = data?.kpis || {};
  const gp = data?.goal_progress || {};

  /* ---- Recruiter quick-filter ---- */
  // Match against name or email; whitespace-insensitive; if no query, no-op.
  const recruiterFilterPredicate = useMemo(() => {
    const q = recruiterQuery.trim().toLowerCase();
    if (!q) return null;
    return (entity) => {
      const hay = `${entity?.name || ''} ${entity?.email || ''}`.toLowerCase();
      return hay.includes(q);
    };
  }, [recruiterQuery]);

  const filteredReportees = useMemo(() => {
    const list = data?.reportees || [];
    const filtered = recruiterFilterPredicate ? list.filter(recruiterFilterPredicate) : list;
    // Stable sort: pinned non-rollup recruiters bubble to the top while
    // preserving their original relative order. Manager rollups stay in place.
    if (pinnedSet.size === 0) return filtered;
    const pinned = filtered.filter((r) => pinnedSet.has(r.user_id) && !r.is_manager_rollup);
    const rest = filtered.filter((r) => !(pinnedSet.has(r.user_id) && !r.is_manager_rollup));
    return [...pinned, ...rest];
  }, [data?.reportees, recruiterFilterPredicate, pinnedSet]);

  const filteredGoalItems = useMemo(() => {
    const items = gp?.items || [];
    const filtered = recruiterFilterPredicate ? items.filter(recruiterFilterPredicate) : items;
    if (pinnedSet.size === 0) return filtered;
    const pinned = filtered.filter((g) => pinnedSet.has(g.user_id));
    const rest = filtered.filter((g) => !pinnedSet.has(g.user_id));
    return [...pinned, ...rest];
  }, [gp?.items, recruiterFilterPredicate, pinnedSet]);

  // Standup view always uses pinned recruiters from the full list (ignoring
  // the mode-tree filter). Falls back to top performers if nothing is pinned.
  const standupReportees = useMemo(() => {
    const list = data?.reportees || [];
    if (pinnedSet.size > 0) {
      // Preserve original sort order; non-rollup pinned only.
      return list.filter((r) => pinnedSet.has(r.user_id) && !r.is_manager_rollup);
    }
    // No pins yet → suggest top 6 by closures so the view is still useful
    return list.filter((r) => !r.is_manager_rollup).slice(0, 6);
  }, [data?.reportees, pinnedSet]);

  // For tree mode, restrict each group's reportee_ids to the filtered set so
  // groups with zero matching members don't render empty headers.
  const filteredGroups = useMemo(() => {
    const groups = data?.groups || [];
    if (!recruiterFilterPredicate) return groups;
    const allowedIds = new Set(filteredReportees.map((r) => r.user_id));
    return groups
      .map((g) => ({
        ...g,
        reportee_ids: (g.reportee_ids || []).filter((rid) => allowedIds.has(rid)),
      }))
      .filter((g) => g.reportee_ids.length > 0);
  }, [data?.groups, filteredReportees, recruiterFilterPredicate]);
  const isEmpty = !loading && (data?.reportees_count || 0) === 0;

  // Breakdown search → set of matching user_ids (name or email contains).
  // We DON'T filter the rendered list with this — see comments on
  // `breakdownSearch` state above. Empty query produces an empty set so
  // the highlight short-circuits.
  const breakdownMatchSet = useMemo(() => {
    const q = breakdownSearch.trim().toLowerCase();
    if (!q) return new Set();
    const matches = new Set();
    for (const r of (data?.reportees || [])) {
      const name = (r.name || '').toLowerCase();
      const email = (r.email || '').toLowerCase();
      if (name.includes(q) || email.includes(q)) matches.add(r.user_id);
    }
    return matches;
  }, [breakdownSearch, data?.reportees]);

  // When breakdown search produces matches, expand any collapsed groups
  // that hold a match and scroll the first match into view. `flashTick`
  // bumps on each new query so React re-runs the scroll effect even if the
  // first match's user_id is unchanged.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (breakdownMatchSet.size === 0) return;
    // Force-expand groups containing matches.
    const groupsWithMatches = (data?.groups || []).filter((g) =>
      (g.reportee_ids || []).some((rid) => breakdownMatchSet.has(rid))
    );
    if (groupsWithMatches.length) {
      setCollapsedGroups((prev) => {
        const next = { ...prev };
        for (const g of groupsWithMatches) next[g.group_id] = false;
        return next;
      });
    }
    // Scroll the FIRST match into view after a tick (let collapsed groups expand first).
    const firstMatchId = [...breakdownMatchSet][0];
    setTimeout(() => {
      const el = document.querySelector(`[data-breakdown-uid="${firstMatchId}"]`);
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 80);
  }, [breakdownMatchSet, flashTick]);

  const headerSubtitle = useMemo(() => {
    if (!data) return null;
    const c = data.reportees_count;
    if (scope === 'self') {
      return c === 1 ? `Your personal stats · ${data.period_label}` : `${data.period_label}`;
    }
    // Admin in tree mode sees the entire organisation, not just their own reporting subtree.
    const isAdminOrgView = mode === 'tree' && currentUser?.role === 'admin';
    const noun = isAdminOrgView
      ? `recruiter${c === 1 ? '' : 's'} across the organisation`
      : mode === 'tree'
      ? `recruiter${c === 1 ? '' : 's'} in your tree`
      : `direct ${c === 1 ? 'reportee' : 'reportees'}`;
    return `${c} ${noun} · ${data.period_label}${data.compare ? ' · vs ' + (PREV_PERIOD_LABEL[period] || 'previous') : ''}`;
  }, [data, mode, period, scope, currentUser?.role]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-4 space-y-4" data-testid="my-team-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Users2 className="w-5 h-5 text-teal-700" />
            {scope === 'self' ? 'My Recruitment' : 'My Team'}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {scope === 'self'
              ? 'Your personal recruitment numbers — switch to Team to see your reportees rolled up.'
              : mode === 'tree' && currentUser?.role === 'admin'
              ? 'Rolled-up performance across every recruiter in your organisation.'
              : mode === 'tree'
              ? 'Rolled-up performance across every recruiter in your reporting tree.'
              : 'Consolidated performance for your direct reportees.'}
            {headerSubtitle ? <span className="ml-1 font-medium text-slate-600">· {headerSubtitle}</span> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Scope toggle (Team rollup vs My recruitment only) — visible only
              for users who actually do recruitment AND have reportees. Pure
              recruiters or pure managers don't need this. */}
          {isRecruitingUser ? (
            <div
              className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5"
              data-testid="mt-scope-tabs"
            >
              <button
                type="button"
                onClick={() => setScope('team')}
                data-testid="mt-scope-team"
                className={`text-xs px-2.5 py-1 rounded-md transition flex items-center gap-1 ${
                  scope === 'team' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
                title="See yourself + your reportees rolled up"
              >
                <Users2 className="w-3 h-3" /> Team
              </button>
              <button
                type="button"
                onClick={() => setScope('self')}
                data-testid="mt-scope-self"
                className={`text-xs px-2.5 py-1 rounded-md transition flex items-center gap-1 ${
                  scope === 'self' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
                title="See only your personal recruitment numbers"
              >
                <Target className="w-3 h-3" /> My recruitment
              </button>
            </div>
          ) : null}

          {/* Mode tabs (Direct vs Tree) — hidden in self scope (tree is meaningless for one row) */}
          {canSeeTree ? (
            <div
              className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5"
              data-testid="mt-mode-tabs"
            >
              <button
                type="button"
                onClick={() => setMode('direct')}
                data-testid="mt-mode-direct"
                className={`text-xs px-2.5 py-1 rounded-md transition flex items-center gap-1 ${
                  mode === 'direct' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Users2 className="w-3 h-3" /> Direct
              </button>
              <button
                type="button"
                onClick={() => setMode('tree')}
                data-testid="mt-mode-tree"
                className={`text-xs px-2.5 py-1 rounded-md transition flex items-center gap-1 ${
                  mode === 'tree' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Network className="w-3 h-3" /> Whole tree
              </button>
            </div>
          ) : null}

          {/* Compare-to-prev toggle — only meaningful when period != 'all' */}
          {period !== 'all' ? (
            <label
              className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 cursor-pointer"
              data-testid="mt-compare-toggle-row"
            >
              <Switch
                checked={compare}
                onCheckedChange={setCompare}
                data-testid="mt-compare-toggle"
                className="scale-75"
              />
              <Label className="text-xs cursor-pointer text-slate-700">
                vs {customRange ? 'previous' : (PREV_PERIOD_LABEL[period] || 'prev')}
              </Label>
            </label>
          ) : null}

          {/* "Today's standup" toggle — quick view of pinned recruiters'
              today activity. Visible only when manager actually has reportees. */}
          {scope === 'team' && (data?.reportees?.length || 0) > 0 ? (
            <button
              type="button"
              onClick={() => setStandupMode((v) => !v)}
              data-testid="mt-standup-toggle"
              title={standupMode ? 'Back to full team view' : "Today's standup view — pinned recruiters' activity today"}
              className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition ${
                standupMode
                  ? 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Coffee className="w-3.5 h-3.5" />
              <span className="font-semibold">Standup</span>
              {pinnedIds.length > 0 ? (
                <span className={`px-1.5 py-px rounded-full text-[10px] font-mono font-bold ${
                  standupMode ? 'bg-amber-700/15 text-amber-900' : 'bg-amber-100 text-amber-700'
                }`}>
                  {pinnedIds.length}
                </span>
              ) : null}
            </button>
          ) : null}

          {/* Custom date-range picker (replaces the old period chips) */}
          <DateRangePicker
            preset={customRange ? null : period}
            customRange={customRange}
            onChange={handleRangeChange}
            testid="mt-range"
          />

          {/* Recruiter quick-filter — name or email match. Hidden when scope=self
              (single row) and when there are no reportees in the dataset. */}
          {scope === 'team' && (data?.reportees?.length || 0) > 1 ? (
            <div className="relative" data-testid="mt-recruiter-search-wrap">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={recruiterQuery}
                onChange={(e) => setRecruiterQuery(e.target.value)}
                placeholder="Search recruiter…"
                className="h-8 w-[180px] pl-8 pr-7 text-xs"
                data-testid="mt-recruiter-search"
              />
              {recruiterQuery ? (
                <button
                  type="button"
                  onClick={() => setRecruiterQuery('')}
                  data-testid="mt-recruiter-search-clear"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
                  aria-label="Clear search"
                >
                  <X className="w-3 h-3" />
                </button>
              ) : null}
            </div>
          ) : null}

          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="mt-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
        </div>
      </header>

      {/* Missing-CTC banner — surfaces joined candidates with no offered_ctc
          so DMs / admins can backfill values inline and watch the Joined CTC
          tile jump in real-time. Same component used on Dashboard. Placed
          OUTSIDE the empty-state branch so admins viewing org-wide totals
          still see it even when they have zero direct reportees. */}
      <MissingCtcBanner />

      {/* Out-of-range CTC alert — admin only, exposes the count of dirty
          records the aggregation cap is masking + link to the cleanup tool. */}
      <CtcOutlierBanner />

      {/* Empty state — no reportees assigned (or, when scope=self, the user doesn't recruit) */}
      {isEmpty ? (
        <Card className="p-10 text-center" data-testid="mt-empty">
          <Users2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h2 className="text-base font-semibold text-slate-800 mb-1">
            {scope === 'self'
              ? 'Recruitment not enabled'
              : mode === 'tree'
              ? 'No team yet'
              : 'No reportees yet'}
          </h2>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            {scope === 'self'
              ? "You don't have recruitment turned on for your account. Ask an admin to tick \"Also does recruitment\" on your user profile."
              : mode === 'tree'
              ? 'No recruiters or team-leads roll up to you yet. Ask an admin to set "Reports to" on the Users page.'
              : "Once an admin sets your team members' \"Reports to\" to your name on the Users page, their stats will roll up here."}
          </p>
        </Card>
      ) : null}

      {!isEmpty && standupMode ? (
        /* "Today's standup" focused view — pinned recruiters with TODAY activity */
        <Card className="overflow-hidden" data-testid="mt-standup-view">
          <div className="px-4 py-2.5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center gap-2 flex-wrap">
            <Coffee className="w-4 h-4 text-amber-700" />
            <h2 className="text-sm font-semibold text-slate-800">
              {pinnedIds.length > 0 ? "Today's standup" : 'Suggested for standup'}
            </h2>
            <span className="text-[10px] text-slate-500 font-mono">
              · {standupReportees.length}{' '}
              {pinnedIds.length > 0 ? 'pinned' : 'top recruiter'}
              {standupReportees.length === 1 ? '' : 's'}
            </span>
            {pinnedIds.length === 0 ? (
              <span className="text-[10px] text-amber-800 ml-auto">
                Tip: ★ pin recruiters to curate your standup view
              </span>
            ) : null}
          </div>
          <div className="p-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="mt-standup-grid">
            {standupReportees.length > 0 ? (
              standupReportees.map((r) => (
                <StandupCard
                  key={r.user_id}
                  r={r}
                  onOpen={() => navigate(buildPerfUrl(r.user_id))}
                />
              ))
            ) : (
              <div className="col-span-full text-center text-sm text-slate-500 py-8">
                No recruiters yet. ★ Pin some on the full view first.
              </div>
            )}
          </div>
          {pinnedIds.length > 0 ? (
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 flex items-center justify-between">
              <span>Showing {standupReportees.length} pinned recruiter{standupReportees.length === 1 ? '' : 's'} · {data?.period_label}</span>
              <button
                type="button"
                onClick={() => setStandupMode(false)}
                className="text-amber-700 hover:text-amber-900 font-semibold"
                data-testid="mt-standup-exit"
              >
                Back to full view →
              </button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {!isEmpty && !standupMode ? (
        <>
          {/* Top performers leaderboard — only renders in team scope when there's data */}
          {scope === 'team' && (data?.top_performers?.length || 0) > 0 ? (
            <Card className="overflow-hidden" data-testid="mt-leaderboard-card">
              <div className="px-4 py-2.5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-600" />
                <h2 className="text-sm font-semibold text-slate-800">Top Performers</h2>
                <span className="text-[10px] text-slate-500 font-mono">
                  · this month · across your tree
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3" data-testid="mt-leaderboard-list">
                {data.top_performers.map((p) => (
                  <LeaderboardCard
                    key={p.user_id}
                    p={p}
                    onOpen={() => navigate(buildPerfUrl(p.user_id))}
                  />
                ))}
              </div>
            </Card>
          ) : null}

          {/* Cold streak watchlist — pipeline but no recent closures */}
          {scope === 'team' && (data?.cold_streak?.length || 0) > 0 ? (
            <Card className="overflow-hidden" data-testid="mt-cold-streak-card-list">
              <div className="px-4 py-2.5 bg-gradient-to-r from-rose-50 to-amber-50 border-b border-rose-100 flex items-center gap-2 flex-wrap">
                <Snowflake className="w-4 h-4 text-rose-500" />
                <h2 className="text-sm font-semibold text-slate-800">Needs a nudge</h2>
                <span className="text-[10px] text-slate-500 font-mono">
                  · pipeline but no closures in 14+ days
                </span>
                <span className="ml-auto text-[10px] text-slate-500 font-mono" data-testid="mt-cold-streak-count">
                  {data.cold_streak.length} {data.cold_streak.length === 1 ? 'recruiter' : 'recruiters'}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-3" data-testid="mt-cold-streak-list">
                {data.cold_streak.map((p) => (
                  <ColdStreakCard
                    key={p.user_id}
                    p={p}
                    onOpen={() => navigate(buildPerfUrl(p.user_id))}
                  />
                ))}
              </div>
            </Card>
          ) : null}

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5" data-testid="mt-kpi-strip">
            <KpiTile
              label="Submissions"
              value={k.submissions_period ?? '—'}
              icon={TrendingUp}
              tone="teal"
              testid="mt-kpi-submissions"
              hint={k.submissions_today ? `${k.submissions_today} today` : 'in period'}
              delta={data?.compare ? k.submissions_delta_pct : undefined}
              deltaSuffix="%"
              deltaTitle={data?.compare ? `vs ${k.submissions_period_prev ?? 0} ${PREV_PERIOD_LABEL[period]}` : undefined}
            />
            <KpiTile
              label="Active"
              value={k.active_candidates ?? '—'}
              icon={Users2}
              tone="slate"
              testid="mt-kpi-active"
              hint="In pipeline"
            />
            <KpiTile
              label="Closures"
              value={k.closures_period ?? '—'}
              icon={Trophy}
              tone="emerald"
              testid="mt-kpi-closures"
              delta={data?.compare ? k.closures_delta_abs : undefined}
              deltaTitle={data?.compare ? `vs ${k.closures_period_prev ?? 0} ${PREV_PERIOD_LABEL[period]}` : undefined}
            />
            <KpiTile
              label="Conv %"
              value={`${k.conversion_pct ?? 0}%`}
              icon={Target}
              tone="violet"
              testid="mt-kpi-conv"
              hint="Closed / total subs"
            />
            <KpiTile
              label="Avg TAT"
              value={k.avg_tat_days != null ? `${k.avg_tat_days}d` : '—'}
              icon={Clock}
              tone="amber"
              testid="mt-kpi-tat"
              hint="Sourced → joined"
            />
            <KpiTile
              label="Interviews today"
              value={k.pending_interviews_today ?? '—'}
              icon={Calendar}
              tone="amber"
              testid="mt-kpi-interviews"
            />
            {showRevenue && (
              <KpiTile
                label="Joined CTC (Total)"
                value={formatINR(k.candidate_ctc_total) || '₹0'}
                icon={IndianRupee}
                tone="emerald"
                testid="mt-kpi-revenue"
                hint={
                  rangeIso
                    ? `${k.joined_count_total || 0} joined · ${rangeIso.start_date} → ${rangeIso.end_date}`
                    : k.joined_count_total != null
                    ? `${k.joined_count_total} joined · all-time`
                    : 'All-time joined'
                }
              />
            )}
          </div>

          {/* Two-column: Goal progress (left, wide) + Pipeline funnel (right) */}
          <div className="grid grid-cols-12 gap-4">
            <Card
              className="col-span-12 xl:col-span-7 overflow-hidden"
              data-testid="mt-team-goal-card"
            >
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2 flex-wrap">
                <Trophy className="w-4 h-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-slate-800">Team Goal Progress</h2>
                <Badge
                  variant="outline"
                  className="text-[10px]"
                  data-testid="mt-team-goal-badge"
                >
                  Team {gp.team_closures_done ?? 0}/{gp.team_goal_total ?? 0} ·{' '}
                  {gp.team_progress_pct ?? 0}%
                </Badge>
                {gp.behind_count > 0 ? (
                  <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">
                    {gp.behind_count} behind
                  </Badge>
                ) : null}
                {gp.achieved_count > 0 ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                    {gp.achieved_count} hit goal
                  </Badge>
                ) : null}
                {pinnedIds.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setPinnedIds([])}
                    data-testid="mt-clear-pins"
                    title="Unpin everyone"
                    className="ml-auto inline-flex items-center gap-1 text-[10px] text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded px-2 py-0.5 transition"
                  >
                    <Star className="w-3 h-3 fill-amber-400" />
                    {pinnedIds.length} pinned
                    <X className="w-3 h-3 ml-0.5" />
                  </button>
                ) : null}
              </div>
              <div className="p-3">
                {/* Aggregate progress bar */}
                <div className="mb-3" data-testid="mt-team-progress-bar">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                      Aggregate progress
                    </span>
                    <span className="text-xs font-mono text-slate-600">
                      {gp.team_closures_done ?? 0} / {gp.team_goal_total ?? 0}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        (gp.team_progress_pct ?? 0) >= 100
                          ? 'bg-emerald-500'
                          : (gp.team_progress_pct ?? 0) >= 60
                          ? 'bg-teal-500'
                          : 'bg-amber-400'
                      }`}
                      style={{ width: `${Math.min(100, gp.team_progress_pct ?? 0)}%` }}
                    />
                  </div>
                </div>

                {/* Per-reportee mini cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {filteredGoalItems.map((g, idx) => {
                    const v = variantStyles[g.variant] || variantStyles.cold;
                    const pinned = pinnedSet.has(g.user_id);
                    return (
                      <div
                        key={g.user_id}
                        className={`rounded-md border-l-4 ${v.row} ${
                          pinned ? 'bg-amber-50/60 border-amber-200' : 'bg-white border-slate-100'
                        } border px-3 py-2`}
                        data-testid={`mt-goal-item-${idx}`}
                        data-pinned={pinned ? 'true' : undefined}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <button
                            type="button"
                            onClick={() => togglePin(g.user_id)}
                            data-testid={`mt-goal-pin-${idx}`}
                            title={pinned ? `Unpin ${g.name}` : `Pin ${g.name} to top`}
                            className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center transition ${
                              pinned ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'
                            }`}
                          >
                            <Star className={`w-3 h-3 ${pinned ? 'fill-amber-400' : ''}`} />
                          </button>
                          <UserAvatar name={g.name} size="sm" />
                          <div className="text-xs font-semibold text-slate-900 truncate flex-1">
                            {g.name}
                          </div>
                          <span className={`text-[10px] font-mono font-semibold ${v.label}`}>
                            {g.closures_done}/{g.goal}
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-1.5 transition-all ${
                              g.variant === 'achieved'
                                ? 'bg-emerald-500'
                                : g.variant === 'ontrack'
                                ? 'bg-teal-500'
                                : g.variant === 'cold'
                                ? 'bg-amber-400'
                                : 'bg-rose-500'
                            }`}
                            style={{ width: `${Math.min(100, g.progress_pct)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {recruiterQuery && filteredGoalItems.length === 0 && (gp.items || []).length > 0 ? (
                    <div className="col-span-full text-center text-xs text-slate-400 py-4 italic" data-testid="mt-goal-no-match">
                      No recruiter matches "{recruiterQuery}".
                    </div>
                  ) : null}
                </div>
              </div>
            </Card>

            <Card
              className="col-span-12 xl:col-span-5 overflow-hidden"
              data-testid="mt-funnel-card"
            >
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-violet-600" />
                <h2 className="text-sm font-semibold text-slate-800">Team Pipeline Funnel</h2>
                <span className="text-[10px] text-slate-500 font-mono">
                  · {data?.period_label}
                </span>
              </div>
              <div className="p-3">
                <FunnelBar stages={data?.funnel || []} />
              </div>
            </Card>
          </div>

          {/* Reportee-wise breakdown — flat (direct mode) or grouped by team-lead (tree mode) */}
          <Card className="overflow-hidden" data-testid="mt-reportees-card">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
              <Users2 className="w-4 h-4 text-teal-700" />
              <h2 className="text-sm font-semibold text-slate-800">
                {mode === 'tree' ? 'Org-tree breakdown' : 'Recruiter-wise breakdown'}
                <span className="ml-1.5 text-xs font-mono text-slate-500">
                  {recruiterQuery
                    ? `${filteredReportees.length} of ${data?.reportees?.length ?? 0}`
                    : (data?.reportees?.length ?? 0)}
                </span>
              </h2>
              {mode === 'tree' && filteredGroups.length ? (
                <span className="text-[10px] text-slate-500 font-mono ml-2">
                  · {filteredGroups.length} group{filteredGroups.length === 1 ? '' : 's'}
                </span>
              ) : null}
              {/* Scroll-to-highlight search. Unlike the global recruiter
                  filter, this preserves full context (no rows hidden) and
                  just expands the matching group + scrolls there. */}
              {scope === 'team' && (data?.reportees?.length || 0) > 1 ? (
                <div className="ml-auto relative" data-testid="mt-breakdown-search-wrap">
                  <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    value={breakdownSearch}
                    onChange={(e) => {
                      setBreakdownSearch(e.target.value);
                      setFlashTick((t) => t + 1);
                    }}
                    placeholder="Jump to recruiter…"
                    className="h-7 w-[180px] pl-7 pr-12 text-[11px]"
                    data-testid="mt-breakdown-search"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setBreakdownSearch('');
                    }}
                  />
                  {breakdownSearch ? (
                    <span className="absolute right-7 top-1/2 -translate-y-1/2 text-[9px] font-mono text-slate-500" data-testid="mt-breakdown-search-count">
                      {breakdownMatchSet.size}
                    </span>
                  ) : null}
                  {breakdownSearch ? (
                    <button
                      type="button"
                      onClick={() => setBreakdownSearch('')}
                      data-testid="mt-breakdown-search-clear"
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
                      aria-label="Clear breakdown search"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-white border-b border-slate-100 text-[9px] uppercase tracking-wide text-slate-500 font-bold sticky top-0 z-10">
              <div className="col-span-3">Recruiter</div>
              <div className="col-span-1 text-center">Subs</div>
              <div className="col-span-1 text-center">Active</div>
              <div className="col-span-1 text-center">Closed</div>
              <div className="col-span-1 text-center">Conv</div>
              <div className="col-span-1 text-center">TAT</div>
              <div className="col-span-1 text-center">Jobs</div>
              <div className="col-span-2">Goal</div>
              <div className="col-span-1 text-right">Open</div>
            </div>
            {/* Use a fixed height (not max-h) so Radix ScrollArea's
                Viewport `h-full` resolves correctly and the internal
                scrollbar actually engages. With max-h the viewport could
                collapse to content height and the section would push the
                whole page instead of scrolling internally. */}
            <ScrollArea className="h-[65vh]" data-testid="mt-reportees-scroll">
              {mode === 'tree' && filteredGroups.length ? (
                // Tree mode: render each group as a collapsible section.
                // We index reportees by id once for O(1) row lookups inside groups.
                (() => {
                  const reporteeById = Object.fromEntries(
                    filteredReportees.map((r) => [r.user_id, r])
                  );
                  let runningIdx = -1;
                  return filteredGroups.map((g) => {
                    const isCollapsed = !!collapsedGroups[g.group_id];
                    const rows = (g.reportee_ids || [])
                      .map((rid) => reporteeById[rid])
                      .filter(Boolean);
                    return (
                      <div key={g.group_id} data-testid={`mt-group-${g.group_id}`}>
                        <button
                          type="button"
                          onClick={() =>
                            setCollapsedGroups((prev) => ({
                              ...prev,
                              [g.group_id]: !prev[g.group_id],
                            }))
                          }
                          data-testid={`mt-group-header-${g.group_id}`}
                          className="w-full flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200 hover:bg-slate-200 transition text-left"
                        >
                          {isCollapsed ? (
                            <ChevronRight className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                          )}
                          <Network className="w-3.5 h-3.5 text-violet-600 flex-shrink-0" />
                          <span className="text-xs font-semibold text-slate-800 truncate">
                            {g.leader.name}
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono">
                            ({g.summary.headcount} {g.summary.headcount === 1 ? 'recruiter' : 'recruiters'})
                          </span>
                          <span className="ml-auto flex items-center gap-2 text-[10px] font-mono text-slate-600">
                            <span title="Submissions">
                              <TrendingUp className="w-3 h-3 inline-block mr-0.5 text-teal-600" />
                              {g.summary.submissions_period}
                            </span>
                            <span title="Closures">
                              <Trophy className="w-3 h-3 inline-block mr-0.5 text-emerald-600" />
                              {g.summary.closures_period}
                            </span>
                            <span title="Conversion">
                              <Target className="w-3 h-3 inline-block mr-0.5 text-violet-600" />
                              {g.summary.conversion_pct}%
                            </span>
                          </span>
                        </button>
                        {!isCollapsed
                          ? rows.map((r) => {
                              runningIdx += 1;
                              return (
                                <ReporteeRow
                                  key={r.user_id}
                                  r={r}
                                  idx={runningIdx}
                                  showDelta={!!data?.compare}
                                  isPinned={pinnedSet.has(r.user_id)}
                                  onTogglePin={togglePin}
                                  viaManagerName={g.via?.[r.user_id] || null}
                                  isHighlighted={breakdownMatchSet.has(r.user_id)}
                                  onOpen={() =>
                                    navigate(buildPerfUrl(r.user_id))
                                  }
                                />
                              );
                            })
                          : null}
                      </div>
                    );
                  });
                })()
              ) : (
                // Direct mode: simple flat list, sorted by closures desc
                filteredReportees.map((r, idx) => (
                  <ReporteeRow
                    key={r.user_id}
                    r={r}
                    idx={idx}
                    showDelta={!!data?.compare}
                    isPinned={pinnedSet.has(r.user_id)}
                    onTogglePin={togglePin}
                    isHighlighted={breakdownMatchSet.has(r.user_id)}
                    onOpen={() => openReportee(r)}
                  />
                ))
              )}
              {recruiterQuery && filteredReportees.length === 0 && (data?.reportees?.length || 0) > 0 ? (
                <div className="px-4 py-6 text-center text-xs text-slate-400 italic" data-testid="mt-breakdown-no-match">
                  No recruiter matches "{recruiterQuery}".
                </div>
              ) : null}
            </ScrollArea>
            {/* Footer hint — only shows when the scroll area is actually
                overflowing. Visually small but answers the "is there more
                below the fold?" question that a hidden scrollbar can
                leave open, especially on dense org charts. */}
            <ScrollHint
              targetTestId="mt-reportees-scroll"
              total={mode === 'tree' ? (data?.reportees?.length || 0) : filteredReportees.length}
              data-testid="mt-reportees-scroll-hint"
            />
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default MyTeam;
